import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Globe, ArrowRight, Phone, Mail } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

export default function Header() {
  const { lang, danishPath, englishPath, localizePath, t } = useLanguage();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1200);
  const [mobileSubmenuOpen, setMobileSubmenuOpen] = useState({
    '/udredning': true
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    const handleResize = () => {
      setIsMobile(window.innerWidth < 1200);
      if (window.innerWidth >= 1200) {
        setIsOpen(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Close menu on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const navItems = [
    { name: t('nav.home'), path: '/' },
    { name: t('nav.adhd'), path: '/adhd' },
    { name: t('nav.autisme'), path: '/autisme' },
    { name: t('nav.udredning'), path: '/udredning' },
    { 
      name: t('nav.psykiatriske_lidelser'), 
      path: '/psykiatriske-lidelser',
      children: [
        { name: t('nav.angst'), path: '/psykiatriske-lidelser/angst' },
        { name: t('nav.ocd'), path: '/psykiatriske-lidelser/ocd' },
        { name: t('nav.depression'), path: '/psykiatriske-lidelser/depression' },
        { name: t('nav.ptsd'), path: '/psykiatriske-lidelser/ptsd' },
        { name: t('nav.personlighedsforstyrrelser'), path: '/psykiatriske-lidelser/personlighedsforstyrrelser' }
      ]
    },
    { name: t('nav.medicinsk_behandling'), path: '/medicinsk-behandling' },
    { name: t('nav.prices'), path: '/priser' },
    { name: t('nav.about'), path: '/om-os' },
    { name: t('nav.vidensbank'), path: '/vidensbank' },
    { name: t('nav.contact'), path: '/kontakt' }
  ];

  const isActive = (path) => {
    const cur = location.pathname;
    const localized = localizePath(path);
    if (path === '/') {
      return cur === '/' || cur === '/en';
    }
    return cur === localized || cur.startsWith(`${localized}/`);
  };

  const isItemActive = (item) => {
    if (isActive(item.path)) return true;
    if (item.children) {
      return item.children.some(child => isActive(child.path));
    }
    return false;
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        isScrolled
          ? 'bg-sand-light/95 backdrop-blur-md border-b border-sand-medium/80'
          : 'bg-sand-light border-b border-sand-medium/40'
      }`}
    >
      {/* Top Banner */}
      <div className="bg-[#223a35] text-white/90 text-xs border-b border-[#1b2f2b]">
        <div className="mx-auto max-w-[1536px] px-4 sm:px-6 lg:px-12 py-1.5 sm:py-2 flex items-center justify-between">
          {/* Contact Info */}
          <div className="flex items-center gap-3 sm:gap-6">
            <a
              href="tel:+4523957707"
              className="flex items-center gap-1.5 text-white/90 hover:text-white transition-colors font-medium text-[11px] sm:text-xs"
              aria-label="Ring til os på +45 23 95 77 07"
            >
              <Phone className="w-3 h-3 sm:w-3.5 sm:h-3.5 shrink-0 text-white/80" aria-hidden="true" />
              <span>+45 23 95 77 07</span>
            </a>
            <a
              href="mailto:kontakt@nordiskpsykiatri.dk"
              className="hidden md:flex items-center gap-1.5 text-white/90 hover:text-white transition-colors font-medium text-[11px] sm:text-xs"
              aria-label="Send e-mail til kontakt@nordiskpsykiatri.dk"
            >
              <Mail className="w-3 h-3 sm:w-3.5 sm:h-3.5 shrink-0 text-white/80" aria-hidden="true" />
              <span className="truncate max-w-[200px] lg:max-w-none">kontakt@nordiskpsykiatri.dk</span>
            </a>
          </div>

          {/* Language Switcher */}
          <div className="flex items-center gap-1.5 sm:gap-2 text-[11px] sm:text-xs">
            <Link
              to={danishPath}
              className={`px-1.5 py-0.5 rounded font-semibold transition-colors hover:text-white cursor-pointer ${
                lang === 'da'
                  ? 'text-white bg-white/20 shadow-2xs font-bold underline underline-offset-2 decoration-2'
                  : 'text-white/70'
              }`}
              aria-label="Dansk version"
            >
              DK
            </Link>
            <span className="text-white/30 text-[10px]">|</span>
            <Link
              to={englishPath}
              className={`px-1.5 py-0.5 rounded font-semibold transition-colors hover:text-white cursor-pointer ${
                lang === 'en'
                  ? 'text-white bg-white/20 shadow-2xs font-bold underline underline-offset-2 decoration-2'
                  : 'text-white/70'
              }`}
              aria-label="English version"
            >
              EN
            </Link>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1536px] px-6 lg:px-12 relative">
        <div className="flex h-20 items-center justify-between">
          {/* Logo */}
          <Link to={localizePath('/')} className="flex items-center gap-2 sm:gap-3 group z-10 shrink-0">
            <img
              src="https://assets.auxodev.dk/assets/7d97f2cb-1a26-4176-9980-5fb38f404ca8/nordisk-psykiatri-kompaslogo.png"
              alt="Nordisk Psykiatri"
              className="h-10 sm:h-12 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation (Only shown above 1200px) */}
          {!isMobile && (
            <nav className="absolute left-1/2 -translate-x-1/2 flex items-center">
              <ul className="flex items-center gap-3 lg:gap-4 xl:gap-6 whitespace-nowrap font-display">
                {navItems.filter(item => item.path !== '/kontakt').map((item) => {
                  const hasChildren = item.children && item.children.length > 0;
                  const active = isItemActive(item);
                  return (
                    <li key={item.path} className={hasChildren ? 'group relative py-2' : 'py-2'}>
                      <div className="flex items-center gap-1">
                        <Link
                          to={localizePath(item.path)}
                          className={`font-display text-base lg:text-[17px] xl:text-lg font-bold transition-all duration-200 relative py-1 hover:text-petrol-medium ${
                            active
                              ? 'text-petrol-dark'
                              : 'text-petrol-dark'
                          }`}
                        >
                          {item.name}
                          {/* Active item marked with short, thin underline */}
                          {active && (
                            <span className="absolute bottom-0 left-0 w-full h-[1.5px] bg-petrol-dark" />
                          )}
                        </Link>
                        {hasChildren && (
                          <i className="fa-light fa-chevron-down text-[9px] text-petrol-dark/50 group-hover:text-petrol-medium transition-transform duration-300 group-hover:rotate-180" />
                        )}
                      </div>

                      {hasChildren && (
                        <div className="absolute left-0 top-full z-50 pt-3 w-64 sm:w-72 origin-top-left transition-all duration-200 opacity-0 scale-95 pointer-events-none group-hover:opacity-100 group-hover:scale-100 group-hover:pointer-events-auto">
                          <div className="rounded-lg bg-sand-light p-2 shadow-xl ring-1 ring-black/5 border border-sand-medium/60">
                            <ul className="space-y-1">
                              {item.children.map((child) => {
                                const childActive = isActive(child.path);
                                return (
                                  <li key={child.path}>
                                    <Link
                                      to={localizePath(child.path)}
                                      className={`block font-display rounded-md px-4 py-2.5 text-base font-bold transition-all duration-200 ${
                                        childActive
                                          ? 'bg-sand-light text-petrol-dark'
                                          : 'text-petrol-dark hover:bg-sand-light/60 hover:text-petrol-medium'
                                      }`}
                                    >
                                      {child.name}
                                    </Link>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </nav>
          )}

          {/* Desktop Compact Rounded Contact Button (Only shown above 1200px) */}
          {!isMobile && (
            <div className="z-10 shrink-0">
              <Link
                to={localizePath('/kontakt')}
                className="inline-flex items-center justify-center rounded-full bg-petrol-dark px-5 py-2.5 text-xs font-semibold uppercase tracking-wider text-sand-light shadow-xs hover:bg-petrol-medium transition-all duration-200 gap-2"
              >
                <span>{t('nav.contact')}</span>
                <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
              </Link>
            </div>
          )}

          {/* Mobile Burger Button (Only shown below 1200px) */}
          {isMobile && (
            <div className="flex items-center gap-4 z-10">
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="p-1 text-petrol-dark transition-colors focus:outline-none"
                aria-label="Toggle menu"
              >
                {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Menu Panel */}
      {isMobile && (
        <div
          className={`overflow-hidden transition-all duration-300 ease-in-out border-sand-medium bg-sand-light shadow-md ${
            isOpen ? 'max-h-[600px] border-t opacity-100' : 'max-h-0 opacity-0 pointer-events-none'
          }`}
        >
          <div className="px-6 py-6">
            <nav className="flex flex-col gap-6">
              <ul className="flex flex-col gap-4">
                {navItems.map((item) => {
                  const hasChildren = item.children && item.children.length > 0;
                  const isSubOpen = !!mobileSubmenuOpen[item.path];
                  const active = isItemActive(item);
                  return (
                    <li key={item.path} className="flex flex-col">
                      <div className="flex items-center justify-between border-b border-sand-medium/60 py-2">
                        <Link
                          to={localizePath(item.path)}
                          className={`font-display text-xl font-bold transition-colors ${
                            active
                              ? 'text-petrol-dark pl-2 border-l-2 border-l-petrol-medium'
                              : 'text-petrol-dark hover:text-petrol-medium'
                          }`}
                        >
                          {item.name}
                        </Link>
                        {hasChildren && (
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              setMobileSubmenuOpen(prev => ({
                                ...prev,
                                [item.path]: !prev[item.path]
                              }));
                            }}
                            className="p-2 text-petrol-dark/50 hover:text-petrol-medium transition-colors cursor-pointer"
                            aria-label="Toggle submenu"
                          >
                            <i className={`fa-light fa-chevron-down text-sm transition-transform duration-300 ${
                              isSubOpen ? 'rotate-180 text-petrol-medium' : ''
                            }`} />
                          </button>
                        )}
                      </div>
                      {hasChildren && (
                        <div className={`grid transition-all duration-300 ease-in-out ${
                          isSubOpen ? 'grid-rows-[1fr] opacity-100 mt-2' : 'grid-rows-[0fr] opacity-0 pointer-events-none'
                        }`}>
                          <div className="overflow-hidden">
                            <ul className="ml-4 flex flex-col gap-1 border-l border-sand-medium pl-4 pb-2">
                              {item.children.map((child) => (
                                <li key={child.path}>
                                  <Link
                                    to={localizePath(child.path)}
                                    className={`block font-display text-lg font-bold py-2 transition-colors ${
                                      isActive(child.path)
                                        ? 'text-petrol-dark'
                                        : 'text-petrol-dark/90 hover:text-petrol-medium'
                                    }`}
                                  >
                                    {child.name}
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>

              {/* Mobile CTA */}
              <Link
                to={localizePath('/kontakt')}
                className="flex w-full items-center justify-center rounded-full bg-petrol-dark py-3 text-center text-xs font-semibold uppercase tracking-wider text-sand-light shadow-xs hover:bg-petrol-medium transition-colors gap-2"
              >
                <span>{t('nav.contact')}</span>
                <ArrowRight className="h-4 w-4" />
              </Link>

              {/* Mobile Contact Quick Links */}
              <div className="pt-2 border-t border-sand-medium/40 flex flex-col gap-2.5 text-xs text-petrol-dark/80">
                <a
                  href="tel:+4523957707"
                  className="flex items-center gap-2.5 hover:text-petrol-dark font-medium transition-colors"
                >
                  <Phone className="w-3.5 h-3.5 text-petrol-medium shrink-0" aria-hidden="true" />
                  <span>+45 23 95 77 07</span>
                </a>
                <a
                  href="mailto:kontakt@nordiskpsykiatri.dk"
                  className="flex items-center gap-2.5 hover:text-petrol-dark font-medium transition-colors"
                >
                  <Mail className="w-3.5 h-3.5 text-petrol-medium shrink-0" aria-hidden="true" />
                  <span>kontakt@nordiskpsykiatri.dk</span>
                </a>
              </div>
            </nav>
          </div>
        </div>
      )}
    </header>
  );
}
